import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export type SparklineProps = {
  data: number[];
  color?: "brand" | "green" | "red" | "blue";
  height?: number;
  className?: string;
  showGradient?: boolean;
  showTooltip?: boolean;
};

export type RatingChartProps = {
  data: { date: string; value: number }[];
  color?: "brand" | "green" | "red" | "blue";
  height?: number;
  className?: string;
  showTooltip?: boolean;
};

type TooltipPayload = {
  value: number;
  payload: { date?: string };
};

function ChartTooltip({
  active,
  payload,
  showDate,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  showDate?: boolean;
}) {
  if (!active || !payload?.length) return null;

  const data = payload[0];
  return (
    <div className="bg-surface-800 border border-surface-600 rounded-lg px-3 py-2 shadow-lg">
      {showDate && data.payload.date && (
        <div className="text-surface-400 text-xs mb-1">Day {data.payload.date}</div>
      )}
      <div className="text-white font-bold">{data.value.toLocaleString()}</div>
    </div>
  );
}

const colors = {
  brand: "#26d9b5",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#3b82f6",
};

export function Sparkline({
  data,
  color = "brand",
  height = 40,
  className = "",
  showGradient = true,
  showTooltip = true,
}: SparklineProps) {
  const chartData = data.map((value, index) => ({ index, value }));
  const strokeColor = colors[color];
  const gradientId = `sparkline-gradient-${color}`;

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={strokeColor} stopOpacity={0.3} />
              <stop offset="100%" stopColor={strokeColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis domain={["dataMin", "dataMax"]} hide />
          {showTooltip && (
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ stroke: strokeColor, strokeWidth: 1, strokeDasharray: "4 4" }}
              isAnimationActive={false}
            />
          )}
          <Area
            type="monotone"
            dataKey="value"
            stroke={strokeColor}
            strokeWidth={2}
            fill={showGradient ? `url(#${gradientId})` : "transparent"}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RatingChart({
  data,
  color = "brand",
  height = 200,
  className = "",
  showTooltip = true,
}: RatingChartProps) {
  const strokeColor = colors[color];
  const gradientId = `rating-chart-gradient-${color}`;

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={strokeColor} stopOpacity={0.4} />
              <stop offset="100%" stopColor={strokeColor} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#838793", fontSize: 12 }}
            dy={10}
          />
          <YAxis
            domain={["dataMin - 50", "dataMax + 50"]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#838793", fontSize: 12 }}
            dx={-10}
            width={50}
          />
          {showTooltip && (
            <Tooltip
              content={<ChartTooltip showDate />}
              cursor={{ stroke: strokeColor, strokeWidth: 1, strokeDasharray: "4 4" }}
              isAnimationActive={false}
            />
          )}
          <Area
            type="monotone"
            dataKey="value"
            stroke={strokeColor}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
